import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, Headphones, Compass, Wrench, ScrollText } from 'lucide-react';
import api from '../../lib/api';
import MarkdownContent from '../../components/ui/MarkdownContent';
import { toast } from 'sonner';

const CONTENT_TYPE_CONFIG = {
  lectura: { icon: BookOpen, label: 'Lectura Consciente', color: '#c9a227' },
  ajuste: { icon: Compass, label: 'Ajuste Quincenal', color: '#7a9a5a' },
  meditacion: { icon: Headphones, label: 'Meditación', color: '#8a7aaa' },
  herramienta: { icon: Wrench, label: 'Herramienta Práctica', color: '#b88a5a' },
};

const extractDriveId = (url) => {
  if (!url) return null;
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match) return match[1];
  const match2 = url.match(/id=([a-zA-Z0-9_-]+)/);
  return match2 ? match2[1] : null;
};

const CampusContentView = ({ contentId, onBack }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlockedDecreto, setUnlockedDecreto] = useState(null);

  useEffect(() => { if (contentId) fetchContent(); }, [contentId]);

  const fetchContent = async () => {
    try {
      const res = await api.get(`/campus/content/${contentId}`);
      setContent(res.data);
      if (!res.data.is_read) {
        const readRes = await api.post(`/campus/content/${contentId}/read`);
        if (readRes.data.unlocked_decreto) setUnlockedDecreto(readRes.data.unlocked_decreto);
      }
    } catch {
      toast.error('No se pudo cargar el contenido');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center">
        <p className="text-[#5c4a2a]/30">Contenido no encontrado</p>
      </div>
    );
  }

  const config = CONTENT_TYPE_CONFIG[content.content_type] || CONTENT_TYPE_CONFIG.lectura;
  const Icon = config.icon;
  const driveId = extractDriveId(content.audio_url);

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="campus-content-view">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#5c4a2a]/30 hover:text-[#5c4a2a]/60 transition-colors text-sm mb-8"
          data-testid="content-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al jardín
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${config.color}15` }}>
              <Icon className="w-5 h-5" style={{ color: config.color }} />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase" style={{ color: `${config.color}` }}>{config.label}</p>
              <p className="text-[#5c4a2a]/25 text-xs">{content.month_key}</p>
            </div>
          </div>
          <h1 className="text-[#5c4a2a]/80 text-2xl font-light leading-relaxed">{content.title}</h1>
        </div>

        {content.content_type === 'meditacion' && driveId && (
          <div className="mb-8 bg-white border border-[#8a7aaa]/15 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <Headphones className="w-5 h-5 text-[#8a7aaa]" />
              <span className="text-[#5c4a2a]/40 text-sm">Meditación guiada</span>
            </div>
            <iframe
              src={`https://drive.google.com/file/d/${driveId}/preview`}
              className="w-full h-20 rounded-lg"
              allow="autoplay"
              title="Meditación"
              data-testid="meditation-player"
            />
          </div>
        )}

        {content.body && (
          <div className="campus-content-body">
            <MarkdownContent content={content.body} className="campus-markdown" />
          </div>
        )}

        {unlockedDecreto && (
          <div className="mt-12 relative">
            <div className="absolute inset-0 bg-[#c9a227]/5 rounded-2xl blur-xl" />
            <div className="relative bg-white border border-[#c9a227]/15 rounded-2xl p-8 text-center">
              <ScrollText className="w-8 h-8 text-[#c9a227]/50 mx-auto mb-4" />
              <p className="text-[#c9a227]/50 text-xs tracking-[0.2em] uppercase mb-3">Decreto desbloqueado</p>
              <p className="text-[#8a6a20] text-lg font-light italic leading-relaxed" data-testid="unlocked-decreto">
                "{unlockedDecreto.text}"
              </p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .campus-markdown h1, .campus-markdown h2, .campus-markdown h3 {
          color: rgba(30,58,95,0.8) !important;
        }
        .campus-markdown p, .campus-markdown li {
          color: rgba(30,58,95,0.6) !important;
        }
        .campus-markdown blockquote {
          border-left-color: #c9a227 !important;
          background: rgba(201,162,39,0.05) !important;
        }
        .campus-markdown blockquote p {
          color: rgba(138,106,32,0.8) !important;
          font-style: italic;
        }
        .campus-markdown strong {
          color: rgba(30,58,95,0.8) !important;
        }
        .campus-markdown a {
          color: #c9a227 !important;
        }
        .campus-markdown hr {
          border-color: rgba(30,58,95,0.1) !important;
        }
        .campus-markdown table th {
          color: rgba(30,58,95,0.7) !important;
          background: rgba(30,58,95,0.04) !important;
        }
        .campus-markdown table td {
          color: rgba(30,58,95,0.6) !important;
        }
        .campus-markdown table tr:hover {
          background: rgba(30,58,95,0.03) !important;
        }
      `}</style>
    </div>
  );
};

export default CampusContentView;
